/* Shared by the storefront and the local order-review validator. No network calls. */
(() => {
  'use strict';
  const styles = Object.freeze({
    D01: { label: 'Shoreline', material: 'Fleece' },
    D02: { label: 'Wayfinder', material: 'Fleece' },
    D03: { label: 'Vintage', material: 'Sherpa' },
    D05: { label: 'Heritage', material: 'Woven' },
    D06: { label: 'Landmark', material: 'Woven' },
    D07: { label: 'Postcard', material: 'Sherpa' },
  });
  const sizes = Object.freeze(['50x60', '60x80']);
  const fields = Object.freeze({
    lakeName: { property: 'Lake name', max: 100, required: true },
    stateProvince: { property: 'State or province', max: 100, required: true },
    country: { property: 'Country', max: 100, required: true },
    nearestTown: { property: 'Nearest town', max: 100, required: true },
    mapReference: { property: 'Map link or coordinates', max: 250 },
    notes: { property: 'Design notes', max: 500 },
  });
  const normalize = (value) => typeof value === 'string' ? value.normalize('NFC').trim() : '';
  const isId = (value) => typeof value === 'string' && /^[1-9]\d{0,19}$/.test(value);
  const isRequestId = (value) => typeof value === 'string' && /^cl_[a-zA-Z0-9-]{16,80}$/.test(value);
  const unique = (values) => new Set(values).size === values.length;

  function timingCopy(config) {
    const basis = config?.timing?.dayBasis;
    if (basis === 'business') return 'Allow an additional 7–10 business days for your custom lake design, before normal production and shipping.';
    if (basis === 'calendar') return 'Allow an additional 7–10 calendar days for your custom lake design, before normal production and shipping.';
    return 'Custom lake designs add 7–10 days before normal production and shipping. The timing details will be confirmed before ordering opens.';
  }

  function proofCopy(config) {
    if (config?.timing?.proofPolicy === 'required') return 'Your approval of a digital proof is required before printing. Time waiting for your approval is additional.';
    if (config?.timing?.proofPolicy === 'not_required') return 'We follow your selected design style using the facts for your lake. LakeBlankets reviews the design before printing; customer proof approval is not required.';
    return 'The proof approval process will be confirmed before ordering opens.';
  }

  function findSelection(config, selection) {
    const candidates = Array.isArray(config?.styles) ? config.styles.filter((style) => style.code === selection?.style) : [];
    if (candidates.length !== 1 || !Object.hasOwn(styles, selection?.style) || !sizes.includes(selection?.size)) return null;
    const style = candidates[0];
    const variants = Array.isArray(style.variants) ? style.variants.filter((variant) => variant.size === selection.size) : [];
    return variants.length === 1 ? { style, variant: variants[0], definition: styles[style.code] } : null;
  }

  function configurationIssues(config, selection) {
    const issues = [];
    if (config?.schemaVersion !== 1) issues.push('Configuration version is not supported.');
    if (config?.currency !== 'USD') issues.push('Custom pricing is only configured for USD.');
    if (config?.previewOnly !== false) issues.push('This is a local preview.');
    if (config?.enableOrdering !== true) issues.push('Custom ordering has not been enabled.');
    if (!['business', 'calendar'].includes(config?.timing?.dayBasis)) issues.push('Business or calendar days must be confirmed.');
    if (!['required', 'not_required'].includes(config?.timing?.proofPolicy)) issues.push('The customer proof policy must be confirmed.');
    if (!/^[a-zA-Z0-9._-]{1,80}$/.test(config?.timing?.copyVersion || '')) issues.push('Approved timing and policy wording needs a version.');
    if (config?.release?.shippingVerified !== true) issues.push('Custom checkout shipping and delivery estimates need verification.');
    if (config?.release?.policiesApproved !== true) issues.push('Custom order scope and terms need approval.');
    const fulfillmentMode = config?.release?.fulfillmentMode;
    if (!['merchant_manual', 'supplier_personalization_hold'].includes(fulfillmentMode)) issues.push('A supported custom fulfillment mode must be confirmed.');
    if (fulfillmentMode === 'merchant_manual') {
      if (config?.release?.automaticFulfillmentDisabled !== true) issues.push('Automatic Shopify fulfillment must be verified disabled for merchant-only custom orders.');
      if (config?.timing?.dayBasis !== 'calendar') issues.push('Merchant-only custom orders require the approved additional 7–10 calendar days.');
      if (config?.timing?.proofPolicy !== 'not_required') issues.push('Merchant-only custom orders must use the approved no-customer-proof policy.');
    }
    const configuredStyles = Array.isArray(config?.styles) ? config.styles : [];
    const productIds = configuredStyles.map((style) => style.productId).filter(Boolean);
    const variantIds = configuredStyles.flatMap((style) => Array.isArray(style.variants) ? style.variants.map((variant) => variant.id).filter(Boolean) : []);
    if (!unique(configuredStyles.map((style) => style.code)) || !unique(productIds) || !unique(variantIds)) issues.push('Custom product or variant mappings are duplicated.');
    const selected = findSelection(config, selection);
    if (!selected) {
      issues.push('Select a configured style and size.');
      return issues;
    }
    const { style, variant } = selected;
    if (!isId(style.productId) || !isId(style.referenceProductId) || style.productId === style.referenceProductId || !/^custom-lake-[a-z0-9-]+$/.test(style.productHandle || '')) issues.push('A separate custom product and normal-price reference are required.');
    if (fulfillmentMode === 'merchant_manual' && style.merchantIsolationVerified !== true) issues.push('The selected custom product needs verified merchant-only fulfillment without automatic supplier submission.');
    if (fulfillmentMode === 'supplier_personalization_hold' && style.holdVerified !== true) issues.push('The selected custom product needs a verified personalization hold.');
    if (!isId(variant.id) || variant.sku !== `LB-CUSTOM-${style.code}-${selection.size}`) issues.push('The custom variant identity is not configured.');
    if (!Number.isSafeInteger(variant.priceCents) || variant.priceCents <= 0 || !Number.isSafeInteger(variant.referencePriceCents) || variant.priceCents !== variant.referencePriceCents) issues.push('The custom price must equal the matching normal blanket price.');
    if (variant.available !== true) issues.push('This custom blanket is not currently available.');
    return issues;
  }

  function validMapReference(value) {
    if (!value) return true;
    // Coordinates are identification data only. Never fetch customer links here.
    const pair = value.match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
    if (pair) return Math.abs(Number(pair[1])) <= 90 && Math.abs(Number(pair[2])) <= 180;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && Boolean(url.hostname) && !url.username && !url.password;
    } catch { return false; }
  }

  function validateInput(input = {}) {
    const values = {};
    const errors = {};
    for (const [name, rule] of Object.entries(fields)) {
      const value = normalize(input[name]);
      values[name] = value;
      if (input[name] != null && typeof input[name] !== 'string') errors[name] = 'Use plain text for this field.';
      else if (rule.required && !value) errors[name] = `Enter the ${rule.property.toLowerCase()}.`;
      else if (Array.from(value).length > rule.max) errors[name] = `Use ${rule.max} characters or fewer.`;
      else if (/[\u0000-\u001f\u007f<>]/.test(value)) errors[name] = 'Use plain text without markup or control characters.';
    }
    if (!errors.mapReference && !validMapReference(values.mapReference)) errors.mapReference = 'Use an https map link or latitude, longitude coordinates.';
    values.style = typeof input.style === 'string' ? input.style : '';
    values.size = typeof input.size === 'string' ? input.size : '';
    if (!Object.hasOwn(styles, values.style)) errors.style = 'Choose a blanket style.';
    if (!sizes.includes(values.size)) errors.size = 'Choose a blanket size.';
    const quantity = ['number', 'string'].includes(typeof input.quantity) ? String(input.quantity) : '';
    values.quantity = /^\d+$/.test(quantity) ? Number(quantity) : NaN;
    if (!Number.isInteger(values.quantity) || values.quantity < 1 || values.quantity > 10) errors.quantity = 'Choose a whole quantity from 1 to 10.';
    values.timingAccepted = input.timingAccepted === true;
    if (!values.timingAccepted) errors.timingAccepted = 'Confirm the additional custom design time.';
    return { ok: Object.keys(errors).length === 0, values, errors };
  }

  function makeProperties(values, config, requestId) {
    if (!isRequestId(requestId)) throw new Error('A stable custom request ID is required.');
    const properties = {};
    for (const [name, rule] of Object.entries(fields)) if (values[name]) properties[rule.property] = values[name];
    properties['Custom design timing'] = timingCopy(config);
    properties['Proof approval'] = proofCopy(config);
    properties._custom_lake_request_id = requestId;
    properties._custom_lake_terms_version = config.timing.copyVersion;
    properties._custom_lake_schema = '1';
    return properties;
  }

  function buildCartItem(config, input, requestId) {
    const result = validateInput(input);
    const blockers = configurationIssues(config, result.values);
    if (!result.ok || blockers.length) return { ok: false, errors: result.errors, blockers };
    if (!isRequestId(requestId)) return { ok: false, errors: {}, blockers: ['A stable custom request ID is required.'] };
    return { ok: true, item: { id: findSelection(config, result.values).variant.id, quantity: result.values.quantity, properties: makeProperties(result.values, config, requestId) } };
  }

  function validateOrderLine(config, line = {}) {
    // Input must be a trusted Shopify line snapshot. This function grants no release.
    const errors = [];
    const matches = (config?.styles || []).flatMap((style) => (style.variants || []).filter((variant) => String(line.variantId) === variant.id).map((variant) => ({ style, variant })));
    if (matches.length !== 1) return { ok: false, state: 'NEEDS_REVIEW', errors: ['Order variant does not match exactly one custom variant.'] };
    const { style, variant } = matches[0];
    if (line.sku !== variant.sku) errors.push('Order SKU does not match its mapped variant.');
    const properties = line.properties && typeof line.properties === 'object' && !Array.isArray(line.properties) ? line.properties : {};
    const input = { style: style.code, size: variant.size, quantity: line.quantity, timingAccepted: true };
    for (const [name, rule] of Object.entries(fields)) input[name] = properties[rule.property];
    const validation = validateInput(input);
    errors.push(...Object.values(validation.errors));
    errors.push(...configurationIssues(config, input));
    if (properties._custom_lake_schema !== '1' || !isRequestId(properties._custom_lake_request_id)) errors.push('Custom request identity is missing or invalid.');
    if (properties._custom_lake_terms_version !== config?.timing?.copyVersion) errors.push('Recorded terms version needs review against the purchase-time configuration.');
    if (properties['Custom design timing'] !== timingCopy(config) || properties['Proof approval'] !== proofCopy(config)) errors.push('Recorded timing or proof policy is missing or different.');
    return { ok: errors.length === 0, state: errors.length ? 'NEEDS_REVIEW' : 'METADATA_VALIDATED_NOT_RELEASED', errors, values: validation.values };
  }

  globalThis.LBCustomLakeCore = Object.freeze({ styles, sizes, fields, normalize, timingCopy, proofCopy, findSelection, configurationIssues, validateInput, buildCartItem, validateOrderLine });
})();
